import PropTypes from "prop-types";
import React from "react";
import "../styles.css";

// Defaults builders
import { DefaultCommissionTier, DefaultCommissionsTier } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Custom Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import TabActions from "@/Layouts/components/Tab/TabSelectable/TabActions";

// Tier Layouts
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierRows from "@/Layouts/components/Tiers/TierRows";

// Custom components
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";

// MUI
import { Collapse, Grid, Stack, Typography } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

const minimumColumns = 1;
const maximumColumns = 7;
const maximumTiers = Number.MAX_VALUE;

import { CONSTANTS } from "../utils";

const getExactMatrixDimensions = (tiers) => {
	let columnsCount = 0;
	let tiersCount = 0;
	columnsCount = tiers[0]?.commissions?.length;
	tiersCount = tiers.length;
	return {
		columns: columnsCount,
		tiers: tiersCount,
	};
};

const getInputToUse = (tiers = [], index = 0) => tiers?.[index]?.["commissions"]?.[0]?.["commission"]?.["input"] || "Percent";
const getLastIndex = (tiers = [], type = "length", defaultValue = 0) => {
	if (type === "length") {
		return tiers?.length ? tiers.length - 1 : defaultValue;
	}
	return tiers?.[0]?.["commissions"]?.length ? tiers[0]["commissions"].length - 1 : defaultValue;
};

const getColumnDeltaStep = ({ finance = false, crossStore = false }) => {
	if (finance) {
		return 0.01;
	}
	return crossStore ? 0.5 : 1;
};

export default function CommissionsMatrix(props) {
	const {
		make = "",
		dealerCodeFS = "",
		title = "Total Variable Gross",
		tiers = [],
		customizedActions = [],
		backendProductsCount = 7,
		applyTiersRetroactively = null,
		afterChargebacks = null,
		crossStore = false,
		finance = false,
		grossError = false,
		hideFinanceActions = false,
		readOnly = false,
		showApplyTiersRetroactivelySwitch = false,
		showChargebacksSwitch = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
		toggleMatrixSwitch = () => {},
		validateAgainstNoCommission = true,
	} = props;

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();

	const localErrorsMessages = getErrorsMessages();

	const headerErrors = errors?.get("units");
	const rowsErrors = errors?.get("pvr");
	const columnsErrors = errors?.get("commissions");

	const minimumColumnsValue = 0;
	const maximumColumnsValue = !finance ? CONSTANTS.MAX_ROWS_INPUT : backendProductsCount;

	// Control to send update actions
	const wasUpdated = React.useRef(false);

	const onTiersUpdate = (updatedTiers) => {
		onActionValidation?.(make, dealerCodeFS, isValid);
		if (wasUpdated.current) {
			onActionUpdate?.(make, dealerCodeFS, updatedTiers, isValid, getExactMatrixDimensions(innerTiers));
		}
	};

	// States
	const [innerTiers, setInnerTiers] = useCallbackState([], onTiersUpdate);

	// Methods
	const handleAddColumn = () => {
		const updatedTiers = [...innerTiers];
		const commissionInput = getInputToUse(updatedTiers, 0);
		const lastIndex = getLastIndex(updatedTiers, "columns");

		updatedTiers.forEach((tier) => {
			tier.commissions.push(DefaultCommissionTier(0, maximumColumnsValue, { commission: 0, input: commissionInput }));
			if (tier?.commissions?.[lastIndex]) {
				tier.commissions[lastIndex]["to"]["value"] = minimumColumnsValue;
			}
		});

		update(clone(updatedTiers));
	};

	const handleDeleteColumn = () => {
		const updatedTiers = [...innerTiers];
		if (updatedTiers?.[0]?.["commissions"]?.length > minimumColumns) {
			updatedTiers.forEach((tier) => {
				tier.commissions.splice(-1);
				const lastIndex = getLastIndex(tier?.commissions);
				tier.commissions[lastIndex]["to"]["value"] = maximumColumnsValue;
			});
		}
		update(clone(updatedTiers));
	};

	const handleAddTier = () => {
		const updatedTiers = [...innerTiers];
		const lastIndex = getLastIndex(updatedTiers, "length", 1);
		const lastInput = getInputToUse(updatedTiers, lastIndex);
		const previousCommissionsLength = updatedTiers?.[lastIndex]?.["commissions"]?.length;

		const commissions = [];
		for (let i = 0; i < previousCommissionsLength; i++) {
			const previousFrom = updatedTiers?.[0]?.["commissions"]?.[i]?.["from"]?.["value"] || 0;
			const previousTo = updatedTiers?.[0]?.["commissions"]?.[i]?.["to"]?.["value"] || 0;
			commissions[i] = DefaultCommissionTier(previousFrom, previousTo, { commission: 0, input: lastInput });
		}

		let newRowFromValue = 0;
		if (updatedTiers?.length && updatedTiers[lastIndex]) {
			newRowFromValue = updatedTiers[lastIndex]?.to?.value + 0.01;
		}

		updatedTiers.push(DefaultCommissionsTier(newRowFromValue, CONSTANTS.MAX_GROSS_INPUT, commissions));

		update(clone(updatedTiers));
	};

	const handleDeleteTier = () => {
		const updatedTiers = [...innerTiers];
		if (updatedTiers.length >= 1) {
			updatedTiers.splice(-1);
		}
		const lastIndex = getLastIndex(updatedTiers, "length", 1);
		updatedTiers[lastIndex].to.value = CONSTANTS.MAX_GROSS_INPUT;
		update(clone(updatedTiers));
	};

	const handleUpdateInput = (_row, _column, location, area, input) => {
		const updatedTiers = [...innerTiers];
		switch (location) {
			case "column":
				break;
			case "row":
				updatedTiers.forEach((tier) => {
					tier.commissions?.forEach((commission) => {
						if (area in commission) {
							commission[area]["input"] = input;
							commission[area]["value"] = 0;
						}
					});
				});
				break;
			default:
				break;
		}
		update(clone(updatedTiers));
	};

	const handleUpdateValue = (row, column, location, area, value) => {
		const updatedTiers = [...innerTiers];
		const updatedValue = value ? Number(value) : 0;
		const columnDeltaStep = getColumnDeltaStep({ finance, crossStore });
		switch (location) {
			case "column":
				updatedTiers.forEach((tier) => {
					tier.commissions?.forEach((commission, key) => {
						if (area in commission && key === column) {
							commission[area]["value"] = updatedValue;
							if (tier.commissions?.[key + 1]) {
								tier.commissions[key + 1]["from"]["value"] = updatedValue + columnDeltaStep;
							}
						}
					});
				});
				break;
			case "row":
				if (area !== "commission") {
					updatedTiers[row][area]["value"] = updatedValue;
					if (updatedTiers?.[row + 1] && area === "to") {
						updatedTiers[row + 1]["from"]["value"] = updatedValue + 0.01;
					}
				} else {
					updatedTiers[row]["commissions"][column][area]["value"] = updatedValue;
				}
				break;
			default:
				break;
		}
		update(clone(updatedTiers));
	};

	const update = (updatedTiers = []) => {
		wasUpdated.current = true;
		setInnerTiers(updatedTiers);
	};

	const mergeErrorsBonusesValidation = (results = new Map(), section = []) => {
		if (!results?.size) {
			return [];
		}
		const needValidateNoCommission = validateAgainstNoCommission || finance;
		const columns = results?.get("COLUMNS") || [];
		if (needValidateNoCommission && section.every((tier) => tier.every((commission) => !commission.commission.value))) {
			return columns.map((column) => {
				return column.map(() => {
					return {
						commission: !readOnly,
					};
				});
			});
		}

		return columns;
	};

	React.useMemo(() => {
		if (innerTiers?.length) {
			validate([
				{
					definition: {
						useKey: "pvr",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: ">=",
									deltas: { Percent: 0.01 }, // Add delta on upper value
									distinct: 1,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "units",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Percent: 0.01 }, // Add delta on upper value
									distinct: 0,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid column tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers?.[0]?.commissions || [],
				},
				{
					definition: {
						useKey: "commissions",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=", // If current <= next is good,
									constraints: [
										{
											on: "commission",
											apply: true,
										},
									],
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=", // If upper > lower is good,
									deltas: { Dollar: 0, Percent: 0 },
								},
							],
							evaluateAfterConditions: {
								// Merge the resulting map to build the errors list result
								mergeErrors: mergeErrorsBonusesValidation,
							},
						},
						errorsMap: ["commission"],
						errorsMapOn: "commissions",
						message: "Incorrect commissions definition",
						validationType: "MATRIX",
					},
					section: innerTiers,
				},
			]);
		}
	}, [innerTiers, validate]);

	React.useEffect(() => {
		setInnerTiers(clone(tiers));
		return () => {
			wasUpdated.current = false;
			setInnerTiers([]);
		};
	}, [
		tiers,
		setInnerTiers,
		showApplyTiersRetroactivelySwitch,
		showChargebacksSwitch,
		applyTiersRetroactively,
		afterChargebacks,
		backendProductsCount,
	]);

	const disableApplyRetroactivelySwitch =
		finance && !crossStore && innerTiers?.[0]?.commissions?.[0]?.commission?.input === "Dollar per Unit";

	return (
		<Grid container item mt={2} xs={12}>
			<Stack direction={"row"} justifyContent={"space-between"} alignItems={"top"} width={1}>
				<Stack direction={"row"} spacing={2}>
					<Typography className="matrix-title">{title}</Typography>
				</Stack>
				{Boolean(finance && Boolean(customizedActions?.length)) && (
					<Stack>
						<TabActions actions={customizedActions} />
					</Stack>
				)}
				{Boolean(finance && !hideFinanceActions) && (
					<TabActions
						actions={[
							<CustomizedSwitches
								key={"applyTiersRetroactively"}
								textLeft={true}
								textChecked={"Apply Retroactively"}
								textUnChecked={"Apply Retroactively"}
								checked={disableApplyRetroactivelySwitch ? true : Boolean(applyTiersRetroactively)}
								disabled={disableApplyRetroactivelySwitch || readOnly}
								onChange={(value) => toggleMatrixSwitch("applyTiersRetroactively", value)}
							/>,
							<CustomizedSwitches
								key={"afterChargebacks"}
								textLeft={true}
								textChecked={"After (Including chargebacks)"}
								textUnChecked={"Before (Excluding chargebacks)"}
								checked={Boolean(afterChargebacks)}
								disabled={readOnly}
								onChange={(value) => toggleMatrixSwitch("afterChargebacks", value)}
							/>,
						]}
						generalProps={{
							height: "30px",
						}}
					/>
				)}
			</Stack>

			<Grid item xs={12}>
				<TierColumns
					titleLabel={!finance ? "Units" : "Prod. Pen"}
					titleContainerProps={{
						justifyContent: "flex-start",
						alignItems: "end",
						sx: { maxWidth: "241px", width: "241px", marginRight: "3px" },
					}}
					titleProps={{
						className: "tiered-title tiered-title-adjustment",
						sx: null,
					}}
					disabledDelete={innerTiers?.[0]?.commissions?.length <= minimumColumns || readOnly}
					disabledAdd={innerTiers?.[0]?.commissions?.length >= maximumColumns || readOnly}
					onClickDelete={() => handleDeleteColumn()}
					onClickAdd={() => handleAddColumn()}
				>
					<Stack direction={"row"} position={"relative"}>
						{innerTiers?.[0]?.commissions?.map((tier, key) => {
							const fromDisabled = Boolean(!key);
							const toDisabled = Boolean(innerTiers?.[0]?.commissions.length === key + 1);
							return (
								<TierColumnsItem
									key={`commission_matrix_column_${make}_${dealerCodeFS}_${key}`}
									index={key}
									title={"Comm."}
								>
									<MatrixColumn
										index={key}
										item={tier}
										errors={headerErrors}
										fromDisabled={fromDisabled}
										toDisabled={toDisabled}
										readOnly={readOnly}
										onActionUpdate={handleUpdateValue}
										crossStore={crossStore}
										finance={finance}
										backendProductsCount={backendProductsCount - 0.01}
									/>
								</TierColumnsItem>
							);
						})}
					</Stack>
				</TierColumns>
			</Grid>
			<Grid item xs={12} mt={1}>
				<TierRows
					showTitle
					title={!finance ? "Front PVR" : "Back PVR"}
					sxTitlePropsContainer={{
						top: 110,
						left: 5,
					}}
					centerTitleAfterRow={1}
					deleteButtonText={"Remove Tier"}
					addButtonText={"Add Tier"}
					deleteButtonDisabled={innerTiers?.length <= 1 || readOnly}
					addButtonDisabled={innerTiers?.length >= maximumTiers || readOnly}
					spaces={{
						mainSection: 235,
						columns: 101,
						totalColumns: innerTiers?.[0]?.commissions?.length || 1,
						totalRows: innerTiers?.length || 1,
						separation: 5.5,
					}}
					rowsAreaProps={{
						ml: "35px",
					}}
					onClickDelete={() => handleDeleteTier()}
					onClickAdd={() => handleAddTier()}
				>
					<TransitionGroup>
						<Collapse>
							<Stack direction="column" rowGap={2}>
								{(innerTiers || [])?.map((tier, key) => {
									const fromDisabled = Boolean(!key);
									const toDisabled = Boolean(innerTiers?.length === key + 1);
									const commissionsLength = innerTiers?.[0]?.commissions?.length || 0;
									const reduction = commissionsLength * 1.8 + 4.8 + commissionsLength;
									const grossErrorOnLastRow = grossError && key === innerTiers.length - 1;

									return (
										<Grid key={`commission_matrix_row_${make}_${dealerCodeFS}_${key}`} container item>
											<MatrixRow
												index={key}
												item={tier}
												fromDisabled={fromDisabled}
												toDisabled={toDisabled}
												spaces={{
													mainSection: 235,
													columns: 101,
													totalColumns: innerTiers?.[0]?.commissions?.length || 1,
													totalRows: innerTiers?.length || 1,
													separation: 5.5,
													reduction,
												}}
												rowsErrors={rowsErrors || []}
												columnsErrors={columnsErrors || []}
												grossErrorOnLastRow={grossErrorOnLastRow}
												crossStore={crossStore}
												finance={finance}
												readOnly={readOnly}
												spacing={"9px"}
												sxMainContent={{ minWidth: "170.5px", marginRight: "3px" }}
												sxCells={{ minWidth: "100.42px" }}
												onActionUpdate={handleUpdateValue}
												onChangeType={handleUpdateInput}
											/>
										</Grid>
									);
								})}
							</Stack>
						</Collapse>
					</TransitionGroup>

					{Boolean(localErrorsMessages?.length || grossError) && (
						<Grid container direction="column" rowSpacing={0.2} mt={1} mb={1} alignItems="flex-start">
							{localErrorsMessages.map((message, key) => (
								<TierAlertError key={`error_commission_matrix_${message}_${key}`} text={message} />
							))}
							{grossError && (
								<TierAlertError
									key={`error_commission_matrix_gross_${make}`}
									text={"Highest commission must be not equal to zero."}
								/>
							)}
						</Grid>
					)}
				</TierRows>
			</Grid>
		</Grid>
	);
}
CommissionsMatrix.propTypes = {
	make: PropTypes.string,
	title: PropTypes.string,
	dealerCodeFS: PropTypes.string,
	tiers: PropTypes.array,
	customizedActions: PropTypes.array,
	backendProductsCount: PropTypes.number,
	applyTiersRetroactively: PropTypes.bool,
	afterChargebacks: PropTypes.bool,
	crossStore: PropTypes.bool,
	finance: PropTypes.bool,
	grossError: PropTypes.bool,
	hideFinanceActions: PropTypes.bool,
	validateAgainstNoCommission: PropTypes.bool,
	showApplyTiersRetroactivelySwitch: PropTypes.bool,
	showChargebacksSwitch: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
	toggleMatrixSwitch: PropTypes.func,
};
